import { z } from 'zod';
import { currencySchema } from '../common/systems.js';
import { isoDateTimeSchema, nullableText, optionalText, requiredText, skuSchema, } from '../common/coercion.js';
/**
 * Pedidos de Janis OMS (F1, F5, F9, F10).
 *
 * ✅ **VERIFICADO el 2026-09-13** contra `oms.json` 1.386.0, bajado en crudo con
 * `curl` + `jq` porque la SPA de documentacion no lo entrega. Antes de eso este
 * archivo declaraba una forma **inventada** y lo decia; lo que no se sabia es
 * cuanto se habia equivocado: `channelId` por `salesChannelReferenceId`,
 * `shippingAddress` por `addresses[]`, `sku`/`quantity`/`unitPrice` por
 * `refId`/`purchasedQuantity`/`purchasedPrice`, y sin `totalAmount`, que Janis
 * exige. F1 habria fallado en su primera llamada real.
 *
 * ## La regla direccional, que aca es lo que decide casi todo
 *
 * - Lo que **RECIBIMOS** (`janisOrderSchema` y sus partes) es deliberadamente
 *   laxo: `passthrough`, casi todo `nullish`, montos sin signo forzado y
 *   cantidades sin minimo. No es descuido — casi todo el `Order` de Janis esta
 *   declarado `nullable`, un reembolso es un monto negativo y una linea anulada
 *   es cantidad cero. Cada campo exigido de mas aca es una via para mandar
 *   TODOS los pedidos a cuarentena y detener F1, F5, F9 y F10 en seco, porque
 *   este schema es el `.parse` del fetch-back.
 * - Lo que **EMITIMOS** (`…EmitSchema` y `janisOrderCreateRequestSchema`) es
 *   `strict` y ademas comprueba lo que `strict` no ve: que el canal sea uno
 *   solo, que los envios referencien lineas que existen y no repitan ninguna, y
 *   que los montos sean enteros. Un campo de mas o una incoherencia aca es un
 *   error NUESTRO y tiene que verse antes de salir, no al pickear.
 *
 * La moneda sigue el mismo criterio: string opaco al recibir, enum del negocio
 * al emitir.
 */
/**
 * Monto monetario de Janis, EN LO QUE RECIBIMOS.
 *
 * Sin `nonnegative` a proposito: un reembolso, un ajuste o un descuento llegan
 * en negativo, y rechazar el pedido entero por eso lo manda a cuarentena por un
 * dato que ademas es correcto. El monto que EMITIMOS es otra cosa y tiene su
 * propio schema (`janisEmittedAmountSchema`), que si exige entero y positivo.
 */
export const janisAmountSchema = z.number({
    invalid_type_error: 'El monto debe ser un numero.',
});
/**
 * Monto que el middleware EMITE hacia Janis.
 *
 * Entero y no negativo: el negocio opera en CLP y el CLP no tiene decimales.
 * Aceptar `399990.5` solo produce descuadres contra SAP, que es el mismo motivo
 * por el que existe `clpAmountSchema` en el contrato del POS.
 */
export const janisEmittedAmountSchema = z
    .number({ invalid_type_error: 'El monto debe ser un numero.' })
    .int('El CLP no tiene decimales: un monto con centavos descuadra contra SAP.')
    .nonnegative('El monto que emitimos no puede ser negativo.');
/**
 * Precio unitario que el middleware EMITE en un item: no negativo y, a
 * diferencia de `janisEmittedAmountSchema`, con decimales. El entero se exige a
 * la linea (`checkItemsInWholePesos`), que es lo que suma al total.
 */
export const janisEmittedUnitPriceSchema = z
    .number({ invalid_type_error: 'El monto debe ser un numero.' })
    .finite()
    .nonnegative('El monto que emitimos no puede ser negativo.');
/**
 * Tolerancia de coma flotante al comprobar que una linea suma pesos enteros:
 * `(2000 / 3) * 3` puede quedar a 1e-13 de 2.000.
 */
const WHOLE_PESO_EPSILON = 1e-6;
/**
 * Cliente del pedido de Janis.
 *
 * ✅ **VERIFICADO el 2026-09-13** contra `oms.json` 1.386.0, schema
 * `OrderCustomer` (descargado en crudo con `curl` + `jq`, porque la SPA de
 * documentacion no lo entrega). Reemplaza la forma que este archivo asumia, que
 * estaba equivocada en los dos campos que mas importan:
 *
 *   | lo que asumiamos | lo que Janis tiene |
 *   | ---------------- | ------------------ |
 *   | `name` (requerido) | `firstName` + `lastName`, los dos opcionales |
 *   | `document`         | `documentNumber`   |
 *
 * 🔴 **No era cosmetico.** `janisOrderCreateRequestSchema` es `strict` y emitia
 * `{ name, document }` hacia `POST /order`. Janis no conoce ninguno de los dos,
 * asi que el nombre y el RUT del cliente **se habrian perdido en cada pedido de
 * F1**, y ademas —porque la identidad del cliente para Janis es justamente
 * `documentType` + `documentNumber` **o** `email`— un pedido de mostrador pagado
 * en efectivo, sin correo, habria quedado sin ningun identificador de cliente.
 *
 * ## La regla de identidad, que ahora se valida antes de emitir
 *
 * Literal del spec: _"The customer must have at least one of the following
 * unique identifiers: `documentType` and `documentNumber`; `email`. It is
 * required if `customerId` or `customerCommerceId` is not passed."_ Es la misma
 * regla que usa F2 para deduplicar en HubSpot, lo que no es casualidad: las dos
 * responden a la misma pregunta, "¿con que se reconoce a esta persona?".
 *
 * ## Por que casi todo es `nullableText`
 *
 * Porque el spec declara `nullable: true` en practicamente todas estas
 * propiedades. Un pedido real llega con `"lastName": null` y con `optionalText`
 * eso es un `invalid_type` que manda el pedido a cuarentena por un campo que el
 * flujo no necesita.
 */
export const janisCustomerSchema = z
    .object({
    /** Id del cliente en el sistema de origen (el `externalId` del POS). */
    commerceId: nullableText(64),
    firstName: nullableText(255),
    lastName: nullableText(255),
    /** Compuesto por Janis. `readOnly`: no se emite. */
    fullname: nullableText(511),
    email: nullableText(255),
    /** Version acortada para mostrar. `readOnly`: no se emite. */
    emailShortened: nullableText(255),
    /** Texto libre: el spec NO declara un enum. En Chile mandamos `RUT`. */
    documentType: nullableText(32),
    documentNumber: nullableText(64),
    /** Pais emisor del documento, ISO 3166-1 alfa-2 segun el ejemplo (`AR`). */
    documentCountry: nullableText(8),
    phone: nullableText(32),
    /** El cliente es una empresa. Cambia que campos lleva el pedido. */
    isCorporate: z.boolean().nullish(),
    corporateName: nullableText(255),
    tradeName: nullableText(255),
    corporateDocument: nullableText(64),
    corporateEmail: nullableText(255),
    corporatePhone: nullableText(32),
})
    // Lo que RECIBIMOS es laxo: el `OrderCustomer` real trae ademas todo el bloque
    // corporativo extendido, `clusters` e `isFirstOrder`, y conservarlos vale mas
    // que rechazar un pedido por un campo que no usamos.
    .passthrough();
/**
 * El mismo cliente, pero para EMITIR (`POST /order`).
 *
 * `strict` y sin los `readOnly` de Janis (`fullname`, `emailShortened`), que
 * mandados de vuelta son un error nuestro. Y con la regla de identidad del spec
 * comprobada **antes** de salir: un pedido sin identificador de cliente no falla
 * en la red, falla con un 400 de Janis que hay que ir a interpretar.
 */
export const janisCustomerEmitSchema = z
    .object({
    commerceId: optionalText(64),
    firstName: optionalText(255),
    lastName: optionalText(255),
    email: z.string().trim().email('El correo del cliente no es valido.').optional(),
    documentType: optionalText(32),
    documentNumber: optionalText(64),
    documentCountry: optionalText(8),
    phone: optionalText(32),
    isCorporate: z.boolean().optional(),
    corporateName: optionalText(255),
    tradeName: optionalText(255),
    corporateDocument: optionalText(64),
})
    .strict()
    .superRefine((value, ctx) => {
    const conDocumento = value.documentType !== undefined && value.documentNumber !== undefined;
    const conCorreo = value.email !== undefined;
    if (!conDocumento && !conCorreo) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Janis exige que el cliente tenga al menos un identificador unico: ' +
                'documentType + documentNumber, o email. Sin ninguno de los dos rechaza el pedido.',
        });
    }
    // Medio documento es peor que ninguno: Janis exige LOS DOS campos juntos y
    // mandar solo el numero deja un identificador que no identifica.
    if (value.documentType === undefined && value.documentNumber !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['documentType'],
            message: 'Un documentNumber sin documentType no identifica al cliente para Janis.',
        });
    }
});
/**
 * Direccion del pedido de Janis.
 *
 * ✅ **VERIFICADO el 2026-09-13** contra `oms.json` 1.386.0, schemas
 * `OrderAddress` + `OrderAddressBase`. Tambien estaba equivocada: **de los siete
 * campos que este archivo declaraba, cinco tienen otro nombre en Janis**.
 *
 *   | lo que asumiamos | lo que Janis tiene |
 *   | ---------------- | ------------------ |
 *   | `street`         | `streetName`       |
 *   | `number`         | `streetNumber`     |
 *   | `province`       | `state`            |
 *   | `country`        | `countryCode`      |
 *   | `zipCode`        | `postalCode`       |
 *
 * O sea que la direccion de despacho de cada pedido de F1 viajaba entera a
 * campos que Janis no tiene. `city` y `complement` eran los dos que calzaban.
 *
 * `OrderAddressBase` **no declara ningun campo requerido**, lo que confirma que
 * la laxitud de este schema era correcta aunque los nombres no lo fueran.
 */
export const janisAddressSchema = z
    .object({
    streetName: nullableText(255),
    streetNumber: nullableText(32),
    complement: nullableText(128),
    neighborhood: nullableText(128),
    city: nullableText(128),
    state: nullableText(128),
    /**
     * `oms.json` 1.394.0 pide alfa-3 ("The 3-digit country code", ejemplo
     * `ARG`) pero Janis no lo valida: en QA hay pedidos con `Chile`. Opaco al
     * recibir; al emitir se normaliza con `janisCountryCode` (apps/api).
     */
    countryCode: nullableText(8),
    postalCode: nullableText(16),
    reference: nullableText(255),
    /** Quien recibe, cuando no es el cliente. */
    receiverName: nullableText(255),
    receiverDocument: nullableText(64),
    /** Tipo de direccion (`shipping`, `billing`). Sin enum en el spec. */
    type: nullableText(32),
    /** Id de la direccion en el CRM Service. `readOnly`. */
    id: nullableText(64),
    /** Id de la direccion en el sistema de origen. */
    commerceId: nullableText(64),
    /**
     * 🔴 **Primero longitud, despues latitud** — al reves de como se escribe
     * una coordenada en casi todas partes. Lo dice el spec literal: _"First
     * element is longitude, second is latitude."_ Invertirlas no falla: pone el
     * pedido en otro pais.
     */
    geolocation: z.tuple([z.number(), z.number()]).nullish(),
})
    .passthrough();
/**
 * La misma direccion, para EMITIR. `strict` y sin los `readOnly`.
 */
export const janisAddressEmitSchema = z
    .object({
    streetName: optionalText(255),
    streetNumber: optionalText(32),
    complement: optionalText(128),
    neighborhood: optionalText(128),
    city: optionalText(128),
    state: optionalText(128),
    countryCode: optionalText(8),
    postalCode: optionalText(16),
    reference: optionalText(255),
    receiverName: optionalText(255),
    receiverDocument: optionalText(64),
    commerceId: optionalText(64),
    /**
     * `[longitud, latitud]`, en ese orden (el de GeoJSON y el que devuelve
     * Janis). 🔴 Sin coordenadas el DOM no ubica la direccion en el poligono de
     * cobertura del transportista y el pedido queda en `needsIntervention`
     * ("Failed to fetch Fulfillment Plan data", QA 2026-10-07): Janis no
     * geocodifica la direccion por su cuenta.
     */
    geolocation: z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]).optional(),
})
    .strict();
/**
 * Linea de un pedido de Janis.
 *
 * ✅ **VERIFICADO 2026-09-13** contra `oms.json` 1.386.0, schema `OrderItem`.
 * Requeridos: **solo `purchasedQuantity` y `purchasedPrice`**. El resto es
 * opcional, incluido el identificador del producto — lo que sorprende y conviene
 * saber: Janis acepta una linea sin `refId`, y el pedido queda con una linea que
 * no se puede pickear.
 *
 * 🔴 Los nombres NO son los que este archivo asumia: `quantity`/`unitPrice` no
 * existen en Janis; son `purchasedQuantity`/`purchasedPrice`. Y el SKU viaja en
 * `refId`, que es el `referenceId` del SKU del catalogo (el mismo que carga
 * F12): es lo que ata F1 con F12 y con el stock de F3.
 */
export const janisOrderItemSchema = z
    .object({
    /** Id de la linea. Janis lo genera si no se manda. */
    id: nullableText(64),
    name: nullableText(255),
    /** `referenceId` del SKU en el catalogo de Janis. Es el enganche con F12. */
    refId: nullableText(64),
    /**
     * objectId del SKU en Janis. No se manda en el alta: lo completa Janis, de
     * forma asíncrona. 🔴 Falta en las lecturas en `new` recién hecho el alta
     * (`g18` P3a/P3b, `p20` E1/E2, `g16` P1a-1) y está en todas desde
     * `readyForPicking`. Es el que trae la reserva de stock
     * (`StockReservation.skuId`), que no habla por referencias: la verificación
     * de la reserva (`checkJanisReservations`) lee el pedido después de
     * planificar.
     */
    skuId: nullableText(64),
    ean: nullableText(32),
    commerceProductId: nullableText(64),
    commerceSkuId: nullableText(64),
    purchasedPrice: janisAmountSchema,
    /**
     * Sin minimo aca: al ACTUALIZAR items, Janis usa `0` para decir "esta linea
     * se quito". Exigir mayor que cero mandaria a cuarentena un pedido al que le
     * anularon una linea, que es un pedido perfectamente normal.
     */
    purchasedQuantity: z.number().nullish(),
    sellingMeasurementUnit: nullableText(16),
    sellingUnitMultiplier: z.number().positive().nullish(),
    /**
     * Precio por unidad de medida, cuando la cantidad es fraccionaria.
     *
     * Se declara aunque el flujo de alta ya lo emita: **quien LEE el pedido lo
     * necesita**. Con `purchasedQuantity: 1` y `sellingUnitMultiplier: 2.35`, el
     * `purchasedPrice` es el neto de la linea entera, no el unitario — asi que
     * un consumidor que tome `purchasedPrice` como precio unitario y
     * `purchasedQuantity` como cantidad reserva 1 unidad al precio de 2,35 m.
     * Entraba por `passthrough`, o sea sin tipo y sin que nadie lo viera.
     */
    purchasedPricePerMeasurementUnit: z.number().nullish(),
    isPicked: z.boolean().nullish(),
})
    .passthrough();
/**
 * Envio del pedido. Es donde vive la bodega, y eso cambia el diseno de F1.
 *
 * ✅ **VERIFICADO 2026-09-13** (`OrderShipping`). Requeridos:
 * **`deliveryEstimateDate` e `items`**.
 *
 * 🔴 **La bodega NO esta en la raiz del pedido**: `Order` no tiene
 * `warehouseId`, `OrderShipping` si. O sea que el modelo de Janis **permite una
 * bodega por envio**, que es justo lo que F1 necesitaba y creia no tener: la
 * nota del mapper decia que "Janis tiene una sola bodega a nivel de pedido" y
 * por eso no mandaba ninguna cuando las lineas mezclaban. Con esto, un pedido
 * con lineas de dos bodegas se puede expresar como dos envios.
 */
/** Linea dentro de un envio, tal como la devuelve Janis. */
export const janisOrderShippingItemSchema = z
    .object({
    /** Posicion de la linea en `items`, base 0. */
    index: z.number().int().nonnegative().nullish(),
    id: nullableText(64),
    quantity: z.number().nullish(),
})
    .passthrough();
export const janisOrderShippingSchema = z
    .object({
    id: nullableText(64),
    /**
     * `store_pickup`, `delivery`, ... Opaco al recibir: el enum del spec
     * (`JANIS_SHIPPING_TYPES`) se exige solo en lo que emitimos.
     */
    type: nullableText(32),
    addressId: nullableText(64),
    addressCommerceId: nullableText(64),
    /**
     * Requerido para Janis al CREAR un envio, y opcional aca a proposito: esto
     * es lo que RECIBIMOS, y un envio que vuelva sin fecha —o con `null`— no
     * puede costar el pedido entero. La exigencia vive en el schema de emision.
     */
    deliveryEstimateDate: isoDateTimeSchema.nullish(),
    dispatchDate: isoDateTimeSchema.nullish(),
    isPickup: z.boolean().nullish(),
    /** `id` INTERNO de la bodega en Janis, no su `referenceId`. */
    warehouseId: nullableText(64),
    /** El `referenceId` de la misma bodega, cuando Janis lo devuelve. */
    warehouseReferenceId: nullableText(64),
    price: janisAmountSchema.nullish(),
    status: nullableText(64),
    /**
     * Id del envio en el Delivery Service (`GET delivery /shipping/{id}`).
     * Existe desde `readyForPicking` en un `store_pickup` (S0, 2026-10-23).
     */
    deliveryId: nullableText(64),
    /**
     * Que parte del pedido va en este envio. Se modela porque es el insumo de
     * las entregas parciales, que en muebles son lo habitual: sin esto no se
     * sabe cuales lineas llegaron. Laxo y `nullish` porque es lo que recibimos.
     */
    items: z.array(janisOrderShippingItemSchema).nullish(),
    /**
     * La tienda de un retiro (`store_pickup`). Laxo: lo que vuelve del pedido
     * planificado (QA 2026-11-01, `g12`) trae lo que mandamos.
     */
    pickupInfo: z
        .object({
        name: nullableText(255),
        warehouseRefId: nullableText(64),
        pickupPointRefId: nullableText(64),
        address: janisAddressSchema.nullish(),
    })
        .passthrough()
        .nullish()
        .catch(null),
})
    .passthrough();
/**
 * Pago del pedido (`OrderPayment`).
 *
 * ⚠️ El spec declara requeridos `amount` **y `status`**, pero marca `status`
 * como `readOnly`. Las dos cosas no pueden ser ciertas a la vez para un alta, y
 * el ejemplo oficial de `POST /order` **no manda `status`**. Se sigue el
 * ejemplo: al emitir no se manda, al recibir es opcional.
 */
export const janisOrderPaymentSchema = z
    .object({
    id: nullableText(64),
    paymentGroup: nullableText(64),
    paymentSystemCommerceId: nullableText(64),
    paymentSystemName: nullableText(128),
    amount: janisAmountSchema,
    status: nullableText(64),
    transactionId: nullableText(128),
})
    .passthrough();
/** Un error de proceso del pedido. Laxo: es un payload de un tercero. */
export const janisOrderErrorSchema = z
    .object({
    process: nullableText(64),
    keyTranslate: nullableText(128),
    message: nullableText(1024),
    details: z.array(z.string()).nullish(),
    dateCreated: isoDateTimeSchema.nullish(),
})
    .passthrough();
/**
 * Pedido completo, tal como vuelve del fetch-back (`GET /order/{id}`).
 *
 * Este es el objeto que se procesa; el webhook solo trae el `id`.
 *
 * ✅ **VERIFICADO 2026-09-13** contra el schema `Order` (68 propiedades; aca se
 * declaran las que el middleware usa y el resto entra por `passthrough`).
 * Requeridos por Janis: `totalAmount` e `items` — pero en lo que RECIBIMOS
 * ninguno se exige salvo `id`, `status` e `items`, porque un pedido que llega
 * raro tiene que poder verse en la UI, no desaparecer en cuarentena.
 *
 * 🔴 **Su salida se GUARDA y se vuelve a parsear**: la foto para SAP de
 * `POST /api/decorapos/dte` (`decorapos_dte_sale.sap_order_input`, en
 * `apps/api`) congela los pedidos ya parseados, y F6 los relee con este mismo
 * schema dias despues. Un cambio aca (un campo que se estrecha, un `.min()`, un
 * `transform` que no devuelve lo mismo sobre su propia salida) no puede dejar de
 * leer esas fotos: lo prueba `apps/api/test/persisted-formats/`. Si el cambio es
 * incompatible, la foto pasa a `version: 2` con un lector de la v1.
 */
export const janisOrderSchema = z
    .object({
    id: requiredText(64),
    /** Id del pedido en el sistema de origen (DecoraPOS, Multivende). */
    commerceId: nullableText(64),
    /**
     * El grupo de pedidos del pedido (oms 1.398.0): su id en Janis (24 hex) y su
     * `commerceId`. Un hijo de una venta agrupada (`docs/pedidos-de-tienda.md`
     * §1.4) los trae desde `new`, con `orderGroupCommerceId` = el numero de la
     * venta en el canal (✅ QA 2026-10-28, `p1-create.jsonl` de
     * `test/fixtures/janis/grupo-2026-10/`). El aviso de un hijo tambien los
     * trae, pero eso se vio en QA sin grabarse: esta en
     * `docs/s0-resultados-pedidos-de-tienda.md` («Grupo de pedidos», Webhooks).
     *
     * 🔴 **Todo pedido de oms >= 1.398.0 trae grupo.** Uno creado sin
     * `orderGroupCommerceId` trae el suyo, con `orderGroupCommerceId ===
     * commerceId` (los pedidos T0 a T7 de S0, `final-T<n>` de
     * `test/fixtures/janis/s0-2026-10/grabaciones/limpieza.jsonl`). Que el campo
     * venga NO dice que el pedido sea parte de una venta agrupada: lo es si
     * `orderGroupCommerceId !== commerceId` (`isSaleGroupChild`), o si esta en
     * la venta guardada del middleware (`sale_group`). Usar `orderGroupId !=
     * null` como senal trataria todos los pedidos como agrupados.
     *
     * `nullish` como todo lo que recibimos: un pedido de antes de oms 1.398.0 no
     * los trae. Ver `janisOrderCreateRequestSchema`.
     */
    orderGroupId: nullableText(64),
    orderGroupCommerceId: nullableText(64),
    /**
     * Estado del pedido. String opaco: son 31 y el catalogo vive en el fixture
     * `janis/order-statuses.json`, no en un enum de este archivo.
     */
    status: requiredText(64),
    /** Canal de venta: Janis identifica el mismo canal de las dos formas. */
    salesChannelId: nullableText(64),
    salesChannelReferenceId: nullableText(64),
    salesChannelName: nullableText(128),
    /** Codigo ISO3 de la moneda, segun el spec. */
    currency: nullableText(16),
    totalAmount: janisAmountSchema.nullish(),
    /** Id del cliente en el CRM Service de Janis. */
    customerId: nullableText(64),
    customerCommerceId: nullableText(64),
    customer: janisCustomerSchema.nullish(),
    /**
     * 🔴 Es un ARREGLO y se llama `addresses`. Este archivo declaraba un
     * `shippingAddress` singular que Janis no tiene: la direccion de despacho de
     * cada pedido de F1 viajaba a un campo inexistente.
     */
    addresses: z.array(janisAddressSchema).nullish(),
    shippings: z.array(janisOrderShippingSchema).nullish(),
    payments: z.array(janisOrderPaymentSchema).nullish(),
    dateCreated: isoDateTimeSchema.nullish(),
    dateModified: isoDateTimeSchema.nullish(),
    items: z.array(janisOrderItemSchema).min(1, 'Un pedido sin lineas no es un pedido.'),
    /**
     * Errores de proceso del pedido (p. ej. el DOM que no pudo planificar).
     * VERIFICADO en QA el 2026-09-25 (`oms-order-needs-intervention.json`):
     * un pedido en `needsIntervention` trae aca `process: "fulfillmentPlan"` y
     * un `keyTranslate` como `dom.errors.noCarriersAvailable`.
     */
    errors: z.array(janisOrderErrorSchema).nullish(),
    /**
     * Las facturas que Janis ya registro del pedido (JEF). Aparece en el primer
     * estado despues de que Janis factura (`invoiced`, o el que sigue si
     * `invoiced` no se alcanzo a ver) y desde ahi no se va (✅ QA 2026-10-31,
     * `retencion-2026-10/grabaciones/p20-descuento-al-facturar.jsonl` y
     * `grupo-2026-10-31/grabaciones/g2-refund-amount-hijo.jsonl`). Antes viene
     * `null`. Es la senal de `isJanisOrderInvoiced`: el estado no sirve, porque
     * el re-embalaje pasa por `needsRepacking` y vuelve a `readyForInvoice`.
     */
    invoices: z
        .array(z.object({ id: nullableText(64), number: nullableText(64) }).passthrough())
        .nullish(),
    /**
     * La bodega de picking forzada en el alta (P60, `janisFulfillmentSiteSchema`).
     * Vuelve igual en el pedido planificado (QA 2026-11-01, `g12`). Tolerado:
     * una forma rara se lee como ausente, nunca tumba el pedido.
     */
    fulfillmentSite: z
        .object({
        picking: z
            .object({
            locationId: nullableText(64),
            warehouseId: nullableText(64),
            warehouseRefId: nullableText(64),
        })
            .passthrough()
            .nullish()
            .catch(null),
    })
        .passthrough()
        .nullish()
        .catch(null),
})
    .passthrough();
/**
 * ¿Es el pedido un HIJO de una venta agrupada (`docs/pedidos-de-tienda.md`
 * §1.4)? Si su grupo NO se llama como el: `orderGroupCommerceId` presente y
 * distinto de su `commerceId`.
 *
 * 🔴 No es `orderGroupId != null`: todo pedido de oms >= 1.398.0 trae grupo, y
 * uno sin agrupar trae el suyo con `orderGroupCommerceId === commerceId` (ver
 * `janisOrderSchema.orderGroupId`). Un pedido de antes de 1.398.0, sin grupo, no
 * es hijo. La venta guardada del middleware (`sale_group`) es la otra fuente, y
 * la que manda para un pedido que el middleware creo. Pura.
 */
export function isSaleGroupChild(order) {
    return (order.orderGroupCommerceId !== undefined &&
        order.orderGroupCommerceId !== null &&
        order.orderGroupCommerceId !== order.commerceId);
}
/**
 * Linea de un pedido que el middleware crea en Janis.
 *
 * 🔴 **`purchasedQuantity` es un ENTERO y Janis rechaza los decimales.** Literal
 * del spec: _"as an integer count of units. Decimal values are rejected"_, y al
 * crear tiene que ser **al menos 1**.
 *
 * Eso choca de frente con DecoraPOS, cuyo `quantitySchema` admite hasta cuatro
 * decimales **a proposito**: hay SKU que se venden por metro (telas, cortes). O
 * sea que una venta de 2,35 m de tela **no se puede expresar** con la cantidad
 * directamente.
 *
 * Janis documenta el camino y el mapper lo implementa (`toJanisOrderCreateRequest`):
 * la cantidad fraccionaria viaja como **multiplicador**, no como cantidad.
 *
 *     2,35 m  ->  purchasedQuantity: 1
 *                 sellingUnitMultiplier: 2.35
 *                 purchasedPricePerMeasurementUnit: <precio por metro>
 *
 * Con el ejemplo textual del spec: _"0.750 kg of a product sold by weight is
 * `purchasedQuantity: 1`, `sellingMeasurementUnit: "kg"`,
 * `sellingUnitMultiplier: 0.750` — never `purchasedQuantity: 0.75`"_.
 */
export const janisOrderCreateItemSchema = z
    .object({
    /** Id de la linea en el sistema de origen. Lo usa `shippings[].items`. */
    id: optionalText(64),
    name: optionalText(255),
    /** `referenceId` del SKU en Janis: el mismo que carga F12. */
    refId: skuSchema,
    /**
     * Precio unitario **NETO**: lo que el cliente paga, ya con el descuento.
     *
     * 🔴 Puede tener decimales, y la LINEA no: `purchasedPrice x
     * purchasedQuantity` tiene que dar pesos enteros (`checkItemsInWholePesos`).
     * Janis calcula el total del pedido desde los items y rechaza el alta con
     * 400 «The payment differs from the total of the order» si supera los
     * pagos, aunque sea por un peso. Con el unitario redondeado, 2.000 netos en
     * 3 u daban 667 x 3 = 2.001: rechazado. Con 666,66… Janis trunca la linea a
     * 1.999,99 y acepta, y SAP (que recibe el neto a 4 decimales) vuelve a dar
     * 2.000 al peso (QA 2026-10-30, `test/fixtures/janis/redondeo-2026-10/`).
     */
    purchasedPrice: janisEmittedUnitPriceSchema,
    /**
     * Precio unitario **DE LISTA**, antes del descuento.
     *
     * Existe en `OrderItem` y es lo que permite que el descuento de la linea
     * llegue a Janis. Sin el, el pedido viajaba con precios de lista y un
     * `totalAmount` menor: descuadrado, y de ahi sale la factura.
     */
    purchasedListPrice: janisEmittedAmountSchema.optional(),
    purchasedQuantity: z
        .number()
        .int('Janis rechaza cantidades decimales: usa sellingUnitMultiplier.')
        .min(1, 'Al crear un pedido, la cantidad de una linea tiene que ser al menos 1.'),
    /**
     * Unidad de medida. Se omite a proposito cuando el POS no la informa: la
     * unidad es del SKU y ya vive en el catalogo de Janis (la carga F12).
     * Mandar una inventada aca la pisaria.
     */
    sellingMeasurementUnit: optionalText(16),
    /** Cantidad real cuando es fraccionaria (2,35 m, 0,75 kg). */
    sellingUnitMultiplier: z.number().positive().optional(),
    /** Precio por unidad de medida, cuando la cantidad es fraccionaria. */
    purchasedPricePerMeasurementUnit: janisEmittedAmountSchema.optional(),
})
    .strict();
/** Linea de un envio: que parte del pedido va en el. */
export const janisOrderShippingItemEmitSchema = z
    .object({
    /** Posicion de la linea en `items`, base 0. Asi lo usa el ejemplo oficial. */
    index: z.number().int().nonnegative(),
    id: optionalText(64),
    /**
     * Entera, por la misma razon que `purchasedQuantity`: Janis rechaza
     * decimales. Que este schema admitiera 2,35 mientras el de la linea lo
     * prohibe era una incoherencia que solo evitaba el mapper.
     */
    quantity: z
        .number()
        .int('Janis rechaza cantidades decimales tambien en las lineas del envio.')
        .min(1, 'Un envio no puede llevar cero unidades de una linea.'),
})
    .strict();
/**
 * Los tipos de envio del spec (`OrderShipping.type`, oms 1.396.0). El
 * contrato de lectura los deja opacos; al emitir solo van estos.
 */
export const JANIS_SHIPPING_TYPES = [
    'delivery',
    'store_pickup',
    'drive_through',
    'express_delivery',
];
export const janisShippingTypeSchema = z.enum(JANIS_SHIPPING_TYPES);
/**
 * La tienda de un envio `store_pickup` (`OrderShipping.pickupInfo`), para
 * EMITIR (`docs/pedidos-de-tienda.md` §3.1). ✅ Janis QA la acepto el
 * 2026-11-01 con `name`, `address` y `warehouseRefId` (`grupo-2026-10-31`,
 * `g12` P0b-bis y `g18`) y completo el carrier solo.
 *
 * - `warehouseRefId` es la REFERENCIA de la bodega (`BOD4`): el spec lo
 *   describe como el `referenceId` de la bodega donde se retira. Es lo unico
 *   obligatorio: S0 (2026-10-23) planifico un retiro con solo esto (forma iii,
 *   §3.1), y el retiro legado (`shipping: pickup`) no trae la direccion de la
 *   tienda.
 * - `name` y `address`: la tienda y su direccion, la misma de `addresses[]`.
 * - `pickupPointRefId` solo cuando la tienda tiene su Pickup Point de WMS
 *   creado: mandarlo sin el deja el pedido en `needsIntervention`
 *   (`dom.errors.pickupPointNotFound`, `reales/oms-order-needs-intervention.json`).
 */
export const janisPickupInfoEmitSchema = z
    .object({
    name: optionalText(255),
    address: janisAddressEmitSchema.optional(),
    warehouseRefId: requiredText(64),
    pickupPointRefId: optionalText(64),
})
    .strict();
/** Envio que el middleware declara al crear el pedido. */
export const janisOrderShippingEmitSchema = z
    .object({
    /** Identidad externa por despacho; id es readOnly en OMS 1.394.1. */
    commerceId: optionalText(64),
    carrierId: optionalText(64),
    slaName: optionalText(128),
    deliveryWindow: z
        .object({
        initialDate: isoDateTimeSchema,
        finalDate: isoDateTimeSchema,
        additionalPrice: janisEmittedAmountSchema,
    })
        .strict()
        .optional(),
    type: janisShippingTypeSchema.optional(),
    addressCommerceId: optionalText(64),
    deliveryEstimateDate: isoDateTimeSchema,
    isPickup: z.boolean().optional(),
    /**
     * Bodega de ESTE envio: su `id` INTERNO en Janis (objectId). Un
     * `referenceId` aca Janis lo descarta sin error. Un pedido con dos bodegas
     * son dos envios.
     */
    warehouseId: optionalText(64),
    /**
     * El costo de despacho que pago el cliente. Sin el, Janis registra el
     * pedido con `totals.shipping` en cero aunque `totalAmount` lo incluya, y el
     * monto a facturar del pedido no calza con el del DTE.
     */
    price: janisEmittedAmountSchema.optional(),
    items: z.array(janisOrderShippingItemEmitSchema).min(1, 'Un envio sin lineas no es un envio.'),
    /** La tienda de un retiro: solo en un `store_pickup`, y siempre en el. */
    pickupInfo: janisPickupInfoEmitSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    if (value.type === 'store_pickup') {
        if (value.isPickup !== true)
            issue('isPickup', 'Un envio store_pickup es isPickup: true.');
        if (value.pickupInfo === undefined)
            issue('pickupInfo', 'Un envio store_pickup lleva pickupInfo con la tienda.');
    }
    else if (value.pickupInfo !== undefined) {
        issue('pickupInfo', 'Solo un envio store_pickup lleva pickupInfo.');
    }
});
/** Pago que el middleware declara al crear el pedido. */
export const janisOrderPaymentEmitSchema = z
    .object({
    paymentGroup: optionalText(64),
    paymentSystemCommerceId: optionalText(64),
    paymentSystemName: optionalText(128),
    amount: janisEmittedAmountSchema,
})
    .strict();
/** `GracePeriodPost` del spec, restringido al modo manual. */
export const janisGracePeriodSchema = z
    .object({
    // Solo manual: el automatico termina la gracia por reloj y soltaria el
    // pedido a fulfillment sin que el POS haya cerrado la venta.
    mode: z.literal('manual'),
})
    .strict();
/**
 * Cuerpo de `POST /order/{id}/cancel`. La cancelacion es de DOS pasos: la
 * primera llamada deja el pedido en `cancelationRequested`; la segunda, con
 * `acknowledgeNoRollback`, lo cancela sin vuelta atras (`oms.json` 1.392.0).
 */
export const janisOrderCancelRequestSchema = z
    .object({
    confirmCancelation: z.literal(true),
    acknowledgeMoneyWillBeReturned: z.literal(true),
    acknowledgeNoRollback: z.literal(true).optional(),
    motive: optionalText(128),
    comment: optionalText(512),
})
    .strict();
/**
 * Cuerpo de `POST /order/{id}/update-items-quantity`: la reduccion de
 * cantidades de una cancelacion parcial (F6, Etapa 2).
 *
 * ✅ Contra el spec (`oms.json` 1.396.0, extracto en
 * `test/fixtures/janis/reales/oms-order-update-items-quantity-spec.json`), 🔴
 * sin corrida en QA. Lo que fija el spec:
 *
 * - `quantity` es la NUEVA cantidad comprada: un valor **absoluto**, no un
 *   delta. Por eso F6 guarda el objetivo antes de llamar, y un reintento
 *   compara contra el.
 * - `remove: true` quita la linea; `remove: false` sin `quantity` falla.
 * - El permiso es `oms:order:write`, y no declara precondiciones de estado.
 *
 * Lo EMITIMOS: estricto. `quantity` entera (Janis rechaza decimales) y cero
 * solo con `remove: true` (y al reves): una linea se quita siempre de la misma
 * forma. 🔴 Que Janis acepte `quantity: 0` junto con `remove: true` se
 * comprueba en la corrida de QA.
 */
export const janisOrderUpdateItemsQuantityRequestSchema = z
    .object({
    items: z
        .array(z
        .object({
        id: requiredText(64),
        quantity: z
            .number({ invalid_type_error: 'La cantidad debe ser un numero.' })
            .int('La cantidad de Janis es entera.')
            .nonnegative('La cantidad no puede ser negativa.'),
        remove: z.boolean().optional(),
    })
        .strict())
        .min(1, 'Un update-items-quantity sin items no cambia nada.'),
})
    .strict()
    .superRefine((value, ctx) => {
    const ids = new Set();
    value.items.forEach((item, indice) => {
        if (ids.has(item.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['items', indice, 'id'],
                message: `La linea ${item.id} viene dos veces.`,
            });
        }
        ids.add(item.id);
        if (item.remove === true && item.quantity !== 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['items', indice, 'quantity'],
                message: 'Una linea que se quita (remove: true) va con cantidad 0.',
            });
        }
        if (item.quantity === 0 && item.remove !== true) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['items', indice, 'remove'],
                message: 'Una linea en cero se quita con remove: true.',
            });
        }
    });
});
/**
 * Respuesta del `update-items-quantity`: `GenericSuccess`, un objeto sin
 * propiedades declaradas. Laxa y tambien `null` (un cuerpo vacio): el exito lo
 * dice el 2xx, y F6 verifica releyendo el pedido.
 */
export const janisOrderUpdateItemsQuantityResponseSchema = z.object({}).passthrough().nullable();
/**
 * La bodega de picking forzada de un pedido (`OrderPost.fulfillmentSite`), para
 * EMITIR (P60, `docs/pedidos-de-tienda.md` §3.1, decidido por JP el
 * 2026-11-01). 🔴 No esta en el spec de `OrderPost`: la forma es la que Janis
 * pidio en su 400 y acepto despues (QA 2026-11-01, `grupo-2026-10-31`, `g12`):
 * un objeto con las TRES identidades de la bodega. Con un string responde 400
 * («Expected a value of type `{locationId,warehouseId,warehouseRefId,...}`»).
 *
 * - `locationId`: el objectId de la location de la bodega (`location` del
 *   listado de bodegas de WMS);
 * - `warehouseId`: el objectId de la bodega;
 * - `warehouseRefId`: su referencia (`BOD4`).
 *
 * Efecto en QA: el DOM no elige (`PIC009: Forced picking point used`), la
 * reserva queda en esa bodega y le gana al perfil DOM. 🔴 **No valida stock**
 * (`g18`): forzado a una bodega sin registro del SKU no reserva, y con menos
 * stock reserva de mas. Por eso F1 verifica la reserva despues de planificar.
 */
export const janisFulfillmentSiteSchema = z
    .object({
    picking: z
        .object({
        locationId: requiredText(64),
        warehouseId: requiredText(64),
        warehouseRefId: requiredText(64),
    })
        .strict(),
})
    .strict();
/**
 * `POST /order` — alta de pedido en Janis.
 *
 * Es por donde entra el pedido de DecoraPOS (F1) ya normalizado.
 * `commerceId` lleva el id del POS: es el enganche para reconciliar y para
 * detectar el pedido duplicado antes de crearlo.
 *
 * ✅ **REESCRITO el 2026-09-13** contra `OrderPost` -> `Order` de `oms.json`
 * 1.386.0 y su ejemplo oficial `PickupOrder`. La version anterior estaba
 * equivocada en **casi todos** los campos: mandaba `channelId` (es
 * `salesChannelReferenceId` o `salesChannelId`), `warehouseId` en la raiz (vive
 * en el envio), `shippingAddress` singular (es `addresses`, arreglo), lineas con
 * `sku`/`quantity`/`unitPrice` (son `refId`/`purchasedQuantity`/
 * `purchasedPrice`) y no mandaba `totalAmount`, que Janis **exige**.
 *
 * Requeridos por el spec: `totalAmount` e `items`. Este schema exige ademas
 * `commerceId` —sin el no hay reconciliacion posible— y el canal, que sin el
 * Janis no sabe donde entra la venta.
 *
 * ## La venta agrupada: `orderGroupCommerceId` (oms 1.398.0)
 *
 * Una venta que necesita dos pedidos o mas (varias fechas, bodegas o modos de
 * entrega) es un GRUPO de pedidos de Janis (`docs/pedidos-de-tienda.md` §1.4):
 * cada hijo lleva `commerceId` = `<numero de venta>-<n>` y
 * `orderGroupCommerceId` = el numero de venta. ✅ Verificado en Janis QA el
 * 2026-10-28 (`test/fixtures/janis/grupo-2026-10/`):
 *
 * - **Janis crea el grupo con el primer hijo** (si no existe uno con ese
 *   `commerceId`) y **asocia los demas**. El hijo trae `orderGroupId` desde
 *   `new`. No se usa `POST /order-group`, que no es idempotente.
 * - Los hijos se crean **de a uno**, en orden de `n`: dos altas en paralelo con
 *   el grupo todavia inexistente podrian crear dos grupos.
 * - `GET /order` acepta `filters[orderGroupCommerceId]` y
 *   `filters[orderGroupId]` (exactos) y `filters[commerceIdStartsWith]` (por
 *   prefijo). 🔴 **No estan declarados en el spec 1.398.0**: se apoyan en lo
 *   grabado, no en el spec. `filters[commerceId]=<venta>` da 0 (compara con el
 *   del hijo) y `filters[search]` no existe (400).
 * - 🔴 **Nunca `POST /order-group/{id}/cancel`**: cancela el grupo y TODOS sus
 *   pedidos con devolucion total. Una cancelacion es siempre de un hijo.
 *
 * Una venta con un solo pedido no manda `orderGroupCommerceId`: el campo se
 * omite, y Janis le crea igual su propio grupo, con su `commerceId` (todo pedido
 * trae grupo, ver `janisOrderSchema.orderGroupId`).
 *
 * ## La barrera de F1 con ventas agrupadas
 *
 * F1 busca el pedido por `commerceId` antes de crearlo y adopta el que
 * encuentra (`findOrderByCommerceId`). 🔴 Con las ventas agrupadas eso no
 * basta: `externalId` es texto libre, asi que la venta normal `PV-344907-2` y el
 * hijo 2 de la venta `PV-344907` comparten `commerceId`. Y como todo pedido
 * trae grupo, un pedido normal `V` ya es un grupo con `commerceId` `V`: Janis
 * asociaria a el los hijos de la venta agrupada `V`.
 *
 * Se adopta un pedido existente solo si su `orderGroupCommerceId` es el numero
 * de la venta (un hijo de ESTA venta, `assertAdoptableSaleChild`), o es igual a
 * su `commerceId` (un pedido normal, `assertAdoptableSingleOrder`). Si no, hay
 * conflicto y va a intervencion: nunca se adopta. Antes del hijo 1, un pedido
 * con `commerceId` = numero de venta impide agrupar
 * (`assertSaleGroupCommerceIdFree`). Los tres en `decorapos/sale-group.ts`.
 */
export const janisOrderCreateRequestSchema = z
    .object({
    commerceId: z
        .string()
        .min(1)
        .max(64)
        .refine((value) => value.trim().length > 0),
    /**
     * El numero de la venta en el canal, en una venta agrupada: Janis crea el
     * grupo con ese `commerceId` o asocia el pedido al que ya existe. Las mismas
     * reglas que `commerceId`. El `orderGroupId` NO se manda: lo asigna Janis.
     */
    orderGroupCommerceId: z
        .string()
        .min(1)
        .max(64)
        .refine((value) => value.trim().length > 0)
        .optional(),
    /**
     * El canal de venta. Janis acepta el `referenceId` o el `id` y **exige uno
     * de los dos**; el middleware manda el que tenga configurado.
     */
    salesChannelReferenceId: optionalText(64),
    salesChannelId: optionalText(64),
    /** Codigo ISO3 (`CLP`). */
    currency: currencySchema,
    /** Total del pedido. Lo exige Janis y antes no se mandaba. */
    totalAmount: janisEmittedAmountSchema,
    customer: janisCustomerEmitSchema,
    addresses: z.array(janisAddressEmitSchema).optional(),
    shippings: z.array(janisOrderShippingEmitSchema).optional(),
    payments: z.array(janisOrderPaymentEmitSchema).optional(),
    items: z.array(janisOrderCreateItemSchema).min(1, 'Un pedido sin lineas no es un pedido.'),
    /**
     * Periodo de gracia (`GracePeriodPost`, `oms.json` 1.392.0): "once the order
     * is reserved it stays in the `inGracePeriod` status (holding its delivery
     * slot) until the grace period ends". Con `mode: manual` lo termina
     * `POST /order/{id}/end-grace-period`. Es la base de la retencion del POS.
     *
     * ⚠️ LEIDO, NO PROBADO: `inGracePeriod` no figura en el enum de estados del
     * propio spec. Por eso la retencion exige OBSERVAR ese estado antes de
     * informar `held`, y queda apagada por configuracion hasta probarlo en QA.
     */
    gracePeriod: janisGracePeriodSchema.optional(),
    /**
     * La bodega de picking forzada (P60): en todo pedido del POS que sale de
     * una sola bodega. Ver `janisFulfillmentSiteSchema`.
     */
    fulfillmentSite: janisFulfillmentSiteSchema.optional(),
})
    // EMITIDO por nosotros: estricto. `janisOrderSchema` (lo que RECIBIMOS del
    // fetch-back) se queda laxo, que es donde la tolerancia sirve.
    .strict()
    .superRefine((value, ctx) => {
    checkSalesChannel(value, ctx);
    checkShippingItems(value, ctx);
    checkItemsInWholePesos(value, ctx);
});
/**
 * Cada linea suma pesos enteros: el unitario puede tener decimales (ver
 * `purchasedPrice`), la linea no. Una linea con centavos descuadra contra SAP,
 * que es por lo que el resto de los montos emitidos son enteros.
 */
function checkItemsInWholePesos(value, ctx) {
    value.items.forEach((item, index) => {
        const linea = item.purchasedPrice * item.purchasedQuantity;
        if (Math.abs(linea - Math.round(linea)) > WHOLE_PESO_EPSILON) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['items', index, 'purchasedPrice'],
                message: `La linea suma ${String(linea)} (purchasedPrice x purchasedQuantity): el CLP no tiene ` +
                    'decimales y una linea con centavos descuadra contra SAP.',
            });
        }
    });
}
/**
 * El canal: **exactamente uno**, no "al menos uno".
 *
 * Mandar los dos y que discrepen es un pedido creado en el canal equivocado, y
 * eso no falla al crear: se descubre al pickear, con la venta hecha. El spec no
 * define cual gana, asi que adivinarlo seria peor que rechazarlo.
 */
function checkSalesChannel(value, ctx) {
    const declarados = [value.salesChannelReferenceId, value.salesChannelId].filter((canal) => canal !== undefined);
    if (declarados.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['salesChannelReferenceId'],
            message: 'Janis exige el canal de venta: salesChannelReferenceId o salesChannelId. ' +
                'Sin el no sabe por que canal entro la venta y rechaza el pedido.',
        });
        return;
    }
    if (declarados.length === 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['salesChannelId'],
            message: 'Se declararon salesChannelReferenceId y salesChannelId a la vez. Janis no documenta ' +
                'cual gana, y si discrepan el pedido entra en el canal equivocado: manda uno solo.',
        });
    }
}
/**
 * Los envios y las lineas tienen que hablar del mismo pedido.
 *
 * `strict` comprueba las CLAVES, no el sentido: sin esto, un envio puede
 * referenciar la linea 99 de un pedido de tres, dos envios pueden llevarse la
 * misma linea —que se despacharia dos veces— y una linea puede quedar sin
 * ningun envio. Los tres son errores nuestros de mapeo, los tres pasan el
 * contrato y los tres se descubren en el muelle.
 *
 * El precedente esta en el mismo paquete: `decoraposOrderCreateRequestSchema`
 * comprueba la aritmetica de sus totales en vez de confiar en el emisor.
 *
 * Lo que a proposito NO se comprueba es que `totalAmount` sea la suma de las
 * lineas: el modelo de totales de Janis es propio —descuentos globales, costo de
 * envio— y exigir la igualdad rechazaria pedidos legitimos.
 */
function checkShippingItems(value, ctx) {
    if (value.shippings === undefined || value.shippings.length === 0) {
        return;
    }
    const vistos = new Set();
    value.shippings.forEach((envio, e) => {
        envio.items.forEach((linea, i) => {
            if (linea.index >= value.items.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['shippings', e, 'items', i, 'index'],
                    message: `El envio referencia la linea ${String(linea.index)} y el pedido tiene ` +
                        `${String(value.items.length)}. Es un error de mapeo nuestro.`,
                });
                return;
            }
            if (vistos.has(linea.index)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['shippings', e, 'items', i, 'index'],
                    message: `La linea ${String(linea.index)} esta en mas de un envio: se despacharia dos veces.`,
                });
                return;
            }
            vistos.add(linea.index);
        });
    });
    if (vistos.size !== value.items.length) {
        const sinEnvio = value.items.map((_, index) => index).filter((index) => !vistos.has(index));
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['shippings'],
            message: `Las lineas ${sinEnvio.join(', ')} no estan en ningun envio: se crearian sin despacho. ` +
                'Si el pedido no lleva envios, no declares ninguno en vez de declararlos incompletos.',
        });
    }
}
/**
 * Sobre de respuesta de los listados de Janis.
 *
 * **VERIFICADO 2026-08-31** contra QA: es un **arreglo pelado**, no `{data:[]}`
 * ni `{items:[]}`. Cierra la pregunta 14 de `docs/preguntas-janis-wms.md`, que
 * preguntaba exactamente esto porque de ahi depende leer bien la barrera de
 * idempotencia.
 *
 * El total NO viene en el cuerpo: viaja en la cabecera `x-janis-total`, segun el
 * contrato transversal de listados (`@janiscommerce/api-list`).
 */
export const janisListResponseSchema = z.array(z.unknown());
//# sourceMappingURL=order.js.map